import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import { listLayoutAPI } from '../services/api';
import {
  ListLayoutEntityType,
  ColumnConfig,
  getDefaultColumns,
} from '../config/listColumns';

const CACHE_KEY_PREFIX = 'list-layout-prefs';
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

// Helper to get localStorage cache
function getLocalStoragePrefs(entityType: ListLayoutEntityType): ColumnConfig[] | null {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}-${entityType}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache valid for 24 hours
      if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
        return data;
      }
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

// Helper to set localStorage cache
function setLocalStoragePrefs(entityType: ListLayoutEntityType, columns: ColumnConfig[]) {
  try {
    localStorage.setItem(
      `${CACHE_KEY_PREFIX}-${entityType}`,
      JSON.stringify({ data: columns, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
}

// Helper to remove localStorage cache
function removeLocalStoragePrefs(entityType: ListLayoutEntityType) {
  try {
    localStorage.removeItem(`${CACHE_KEY_PREFIX}-${entityType}`);
  } catch {
    // Ignore storage errors
  }
}

export function useListLayoutPreferences(entityType: ListLayoutEntityType) {
  const queryClient = useQueryClient();
  const queryKey = ['list-layout-preferences', entityType];

  // Fetch preferences from server
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await listLayoutAPI.get(entityType);
      return response.data.data.columns as ColumnConfig[];
    },
    staleTime: STALE_TIME,
    gcTime: 30 * 60 * 1000, // 30 minutes
    placeholderData: () => {
      // Use localStorage cache or defaults as placeholder
      const cached = getLocalStoragePrefs(entityType);
      return cached || getDefaultColumns(entityType);
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Update localStorage when server data arrives
  useEffect(() => {
    if (query.data) {
      setLocalStoragePrefs(entityType, query.data);
    }
  }, [query.data, entityType]);

  // Save mutation with optimistic update
  const saveMutation = useMutation({
    mutationFn: (columns: ColumnConfig[]) => {
      // Only send key, visible, order to the server
      const columnsToSave = columns.map(({ key, visible, order }) => ({
        key,
        visible,
        order,
      }));
      return listLayoutAPI.update(entityType, columnsToSave);
    },
    onMutate: async (newColumns) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousColumns = queryClient.getQueryData<ColumnConfig[]>(queryKey);

      // Optimistically update both caches
      queryClient.setQueryData(queryKey, newColumns);
      setLocalStoragePrefs(entityType, newColumns);

      return { previousColumns };
    },
    onError: (_err, _newColumns, context) => {
      // Rollback on error
      if (context?.previousColumns) {
        queryClient.setQueryData(queryKey, context.previousColumns);
        setLocalStoragePrefs(entityType, context.previousColumns);
      }
    },
    onSettled: () => {
      // Always refetch after mutation settles
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: () => listLayoutAPI.reset(entityType),
    onSuccess: () => {
      const defaults = getDefaultColumns(entityType);
      queryClient.setQueryData(queryKey, defaults);
      removeLocalStoragePrefs(entityType);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Compute visible columns sorted by order
  const visibleColumns = useMemo(() => {
    const columns = query.data || getDefaultColumns(entityType);
    return columns
      .filter((col) => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [query.data, entityType]);

  // All columns sorted by order (for settings modal)
  const allColumns = useMemo(() => {
    const columns = query.data || getDefaultColumns(entityType);
    return [...columns].sort((a, b) => a.order - b.order);
  }, [query.data, entityType]);

  // Wrapper that uses mutateAsync and waits for refetch
  const saveColumnsAsync = async (columns: ColumnConfig[]) => {
    await saveMutation.mutateAsync(columns);
    // Force refetch to ensure we have the latest data
    await queryClient.refetchQueries({ queryKey });
  };

  // Wrapper for reset that uses mutateAsync
  const resetToDefaultAsync = async () => {
    await resetMutation.mutateAsync();
    // Force refetch to ensure we have the latest data
    await queryClient.refetchQueries({ queryKey });
  };

  return {
    columns: allColumns,
    visibleColumns,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    saveColumns: saveColumnsAsync,
    saveColumnsAsync,
    resetToDefault: resetToDefaultAsync,
    isSaving: saveMutation.isPending,
    isResetting: resetMutation.isPending,
  };
}
